import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { usePlant } from "@/context/PlantContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, AlertTriangle, PackagePlus, History, Merge } from "lucide-react";
import { toast } from "sonner";
import { formatLocalDateTime } from "@/lib/dates";
import InventoryImportDialog from "@/components/InventoryImportDialog";
import MergeDuplicatesDialog from "@/components/MergeDuplicatesDialog";
import { FileSpreadsheet } from "lucide-react";

const CATS = ["cement", "sand", "man_dust", "aggregate_7mm", "aggregate_10mm", "aggregate_14mm", "aggregate_20mm", "exposed_rock", "admixture"];
// Categories weighed in tonnes at the plant (bulk silos + stockpiles).
// Values in the DB stay in kg to match mix-design dosages (kg per m³).
// The UI just presents them in tonnes for readability.
const BULK_CATS = new Set(["cement", "sand", "man_dust", "aggregate_7mm", "aggregate_10mm", "aggregate_14mm", "aggregate_20mm", "exposed_rock"]);
const isBulk = (cat) => BULK_CATS.has(cat);
const displayUnit = (item) => (isBulk(item.category) ? "t" : item.unit);
const displayQty = (item) => (isBulk(item.category) ? Math.round(((item.quantity || 0) / 1000) * 100) / 100 : item.quantity || 0);
// kg → tonnes for display, tonnes → kg for save
const toDisplayQty = (item) => (isBulk(item.category) ? (item.quantity || 0) / 1000 : (item.quantity || 0));
const toDisplayThreshold = (item) => (isBulk(item.category) ? (item.low_stock_threshold || 0) / 1000 : (item.low_stock_threshold || 0));
const toDisplayCost = (item) => (isBulk(item.category) ? (item.unit_cost || 0) * 1000 : (item.unit_cost || 0));

const empty = { sku: "", name: "", category: "cement", unit: "kg", quantity: 0, low_stock_threshold: 1000, unit_cost: 0, gl_code: "", silo: "", capacity_kg: "" };

export default function Inventory() {
  const { plantId } = usePlant();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  // Form is captured in display units (tonnes for bulk, kg/L for admixtures)
  // and converted back to kg on save.
  const [form, setForm] = useState(empty);
  const [adjust, setAdjust] = useState({ id: null, delta: 0 });
  // Inline quick-edit for the Cost cell — double-click a row's cost to change it
  // without opening the full dialog. Saves on blur or Enter.
  const [quickEdit, setQuickEdit] = useState({ id: null, value: "" });
  // v3.16 · Material Receipting (goods-in). Adds to on-hand, optionally
  // updates unit cost, and writes an audit row to inventory_receipts.
  const emptyReceipt = { inventory_id: "", supplier: "", docket_ref: "", quantity: "", unit_cost: "", notes: "" };
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receipt, setReceipt] = useState(emptyReceipt);
  const [receiptSaving, setReceiptSaving] = useState(false);
  const [receipts, setReceipts] = useState([]);

  const loadReceipts = () =>
    api
      .get(`/inventory/receipts?limit=50${plantId ? `&plant_id=${plantId}` : ""}`)
      .then((r) => setReceipts(r.data))
      .catch(() => setReceipts([]));

  const load = () => {
    api.get(`/inventory${plantId ? `?plant_id=${plantId}` : ""}`).then((r) => setList(r.data));
    loadReceipts();
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [plantId]);

  const openNew = () => { setEditing(null); setForm({ ...empty }); setOpen(true); };
  const openEdit = (i) => {
    setEditing(i);
    // Convert kg → tonnes for editing when bulk category
    setForm({
      ...empty,
      ...i,
      quantity: toDisplayQty(i),
      low_stock_threshold: toDisplayThreshold(i),
      unit_cost: toDisplayCost(i),
      unit: isBulk(i.category) ? "t" : (i.unit || "kg"),
      silo: i.silo || "",
      capacity_kg: i.capacity_kg ? Number(i.capacity_kg) / 1000 : "",
    });
    setOpen(true);
  };
  const remove = async () => {
    if (!editing) return;
    if (!window.confirm(`Delete ${editing.sku} · ${editing.name}? This removes the item and its stock level.`)) return;
    try { await api.delete(`/inventory/${editing.id}`); toast.success(`${editing.sku} deleted`); setOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const save = async () => {
    try {
      const bulk = isBulk(form.category);
      // Back to kg for storage; keep admixture values as-is (L or kg).
      const payload = {
        ...form,
        quantity: bulk ? Number(form.quantity || 0) * 1000 : Number(form.quantity || 0),
        low_stock_threshold: bulk ? Number(form.low_stock_threshold || 0) * 1000 : Number(form.low_stock_threshold || 0),
        unit_cost: bulk ? Number(form.unit_cost || 0) / 1000 : Number(form.unit_cost || 0),
        unit: bulk ? "kg" : (form.unit || "kg"),
        silo: (form.silo || "").trim().toUpperCase() || null,
        capacity_kg: form.capacity_kg === "" || form.capacity_kg == null ? null : Number(form.capacity_kg) * 1000,
      };
      if (editing) await api.put(`/inventory/${editing.id}`, payload);
      else await api.post("/inventory", { ...payload, plant_id: plantId });
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const doAdjust = async (item, deltaDisplay) => {
    if (!deltaDisplay) return;
    const delta = isBulk(item.category) ? deltaDisplay * 1000 : deltaDisplay;
    try {
      await api.post(`/inventory/${item.id}/adjust`, { quantity_delta: delta, reason: "manual +/-" });
      toast.success(`Adjusted ${deltaDisplay > 0 ? "+" : ""}${deltaDisplay} ${displayUnit(item)}`);
      load();
      setAdjust({ id: null, delta: 0 });
    } catch (e) { toast.error(e.response?.data?.detail || "Adjust failed"); }
  };
  const [countTarget, setCountTarget] = useState(null);
  const [countForm, setCountForm] = useState({ amount: "", reason: "" });
  const openCount = (item) => { setCountTarget(item); setCountForm({ amount: String(displayQty(item)), reason: "Physical count / silo reading" }); };
  const saveCount = async () => {
    const v = parseFloat(countForm.amount);
    if (isNaN(v) || v < 0) return toast.error("Enter the actual stock on hand (0 or more)");
    try {
      await api.post(`/inventory/${countTarget.id}/adjust`, { mode: "set", amount: isBulk(countTarget.category) ? v * 1000 : v, reason: countForm.reason });
      toast.success(`${countTarget.name} set to ${v} ${displayUnit(countTarget)}`);
      setCountTarget(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Correction failed"); }
  };

  const openReceipt = () => {
    setReceipt({ ...emptyReceipt, inventory_id: list[0]?.id || "" });
    setReceiptOpen(true);
  };
  const receiptItem = list.find((x) => x.id === receipt.inventory_id) || null;
  const submitReceipt = async () => {
    if (!receipt.inventory_id) { toast.error("Pick a material"); return; }
    if (!receipt.supplier?.trim()) { toast.error("Supplier is required"); return; }
    const qtyDisp = parseFloat(receipt.quantity);
    if (!Number.isFinite(qtyDisp) || qtyDisp <= 0) { toast.error("Quantity must be greater than zero"); return; }
    const item = receiptItem;
    if (!item) return;
    const bulk = isBulk(item.category);
    // UI captures tonnes for bulk / L or kg for admix; store in item's unit (kg).
    const storedQty = bulk ? qtyDisp * 1000 : qtyDisp;
    const payload = {
      supplier: receipt.supplier.trim(),
      docket_ref: receipt.docket_ref?.trim() || "",
      quantity: storedQty,
      notes: receipt.notes || "",
    };
    if (receipt.unit_cost !== "" && receipt.unit_cost !== null) {
      const cd = parseFloat(receipt.unit_cost);
      if (Number.isFinite(cd) && cd >= 0) {
        // display cost is $/tonne for bulk → convert to $/kg for storage
        payload.unit_cost = bulk ? cd / 1000 : cd;
      }
    }
    setReceiptSaving(true);
    try {
      await api.post(`/inventory/${item.id}/receipt`, payload);
      toast.success(`Received ${qtyDisp} ${displayUnit(item)} of ${item.name}`);
      setReceiptOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to book receipt");
    } finally {
      setReceiptSaving(false);
    }
  };

  const saveQuickCost = async (item) => {
    if (quickEdit.id !== item.id) return;
    const displayValue = parseFloat(quickEdit.value);
    if (isNaN(displayValue) || displayValue < 0) {
      setQuickEdit({ id: null, value: "" });
      return;
    }
    // Store in kg for bulk items; display was in tonnes so $/t → $/kg
    const storedCost = isBulk(item.category) ? displayValue / 1000 : displayValue;
    if (storedCost === (item.unit_cost || 0)) {   // no change
      setQuickEdit({ id: null, value: "" });
      return;
    }
    try {
      await api.put(`/inventory/${item.id}`, {
        sku: item.sku,
        name: item.name,
        category: item.category,
        unit: item.unit,
        quantity: item.quantity,
        low_stock_threshold: item.low_stock_threshold,
        unit_cost: storedCost,
        gl_code: item.gl_code || "",
        plant_id: item.plant_id,
        silo: item.silo || null,
        capacity_kg: item.capacity_kg ?? null,
      });
      toast.success(`${item.sku} cost updated`);
      setQuickEdit({ id: null, value: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
      setQuickEdit({ id: null, value: "" });
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="inventory-page">
      <div className="flex items-end justify-between">
        <div><div className="label-tag">MATERIALS</div><h1 className="heading text-3xl font-black uppercase">Inventory</h1></div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="receipt-goods-btn"
            onClick={openReceipt}
            variant="outline"
            className="rounded-none border-primary-orange text-primary-orange hover:bg-primary-orange hover:text-black uppercase tracking-widest text-xs font-bold"
          >
            <PackagePlus className="w-4 h-4 mr-1" />Receipt Goods
          </Button>
          <Button data-testid="import-inv-btn" variant="outline" onClick={() => setImportOpen(true)} className="rounded-none border-industrial uppercase tracking-widest text-xs font-bold"><FileSpreadsheet className="w-4 h-4 mr-1 text-primary-orange" />Import</Button>
          <Button data-testid="merge-dupes-btn" variant="outline" onClick={() => setMergeOpen(true)} className="rounded-none border-industrial uppercase tracking-widest text-xs font-bold"><Merge className="w-4 h-4 mr-1 text-primary-orange" />Merge Duplicates</Button>
          <Button data-testid="new-inv-btn" onClick={openNew} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold"><Plus className="w-4 h-4 mr-1" />New Item</Button>
        </div>
      </div>

      <div className="card-industrial overflow-x-auto">
        <div className="p-3 border-b border-industrial text-[11px] text-muted-industrial mono">
          Click any row to edit <span className="text-primary-orange">unit cost, GL code, threshold</span>. Or double-tap the Cost cell for a quick inline edit.
        </div>
        <table className="w-full text-sm">
          <thead className="border-b border-industrial"><tr className="text-left">
            <th className="p-3 label-tag">SKU</th><th className="p-3 label-tag">Name</th><th className="p-3 label-tag">Category</th>
            <th className="p-3 label-tag text-right">On Hand</th><th className="p-3 label-tag text-right">Threshold</th>
            <th className="p-3 label-tag text-right">Cost</th><th className="p-3 label-tag">GL</th><th className="p-3 label-tag text-right">Adjust</th><th className="p-3 label-tag text-right">Actions</th>
          </tr></thead>
          <tbody>
            {list.map((i) => {
              const low = i.quantity < i.low_stock_threshold;
              const qty = toDisplayQty(i);
              const thr = toDisplayThreshold(i);
              const cost = toDisplayCost(i);
              const unit = displayUnit(i);
              const bulk = isBulk(i.category);
              const inlineEditing = quickEdit.id === i.id;
              return (
                <tr
                  key={i.id}
                  data-testid={`inv-row-${i.sku}`}
                  className={`border-b border-industrial hover:bg-surface-2 cursor-pointer ${low ? "bg-orange-950/10" : ""}`}
                  onClick={() => openEdit(i)}
                >
                  <td className="p-3 mono">{i.sku}</td>
                  <td className="p-3">{i.name}{low && <AlertTriangle className="w-3.5 h-3.5 text-primary-orange inline ml-1.5" />}</td>
                  <td className="p-3 mono text-xs uppercase text-muted-industrial">{i.category}</td>
                  <td className={`p-3 mono text-right ${low ? "text-primary-orange" : ""}`}>
                    {qty.toLocaleString(undefined, { maximumFractionDigits: bulk ? 2 : 0 })} {unit}
                  </td>
                  <td className="p-3 mono text-right text-xs text-muted-industrial">
                    {thr.toLocaleString(undefined, { maximumFractionDigits: bulk ? 2 : 0 })} {unit}
                  </td>
                  {/* Cost cell — double-click for inline quick edit so a batcher can
                      tweak prices without opening the dialog. */}
                  <td
                    className="p-3 mono text-right"
                    onClick={(e) => { e.stopPropagation(); }}
                    onDoubleClick={(e) => { e.stopPropagation(); setQuickEdit({ id: i.id, value: cost.toString() }); }}
                    title="Double-click to edit price inline"
                  >
                    {inlineEditing ? (
                      <div className="flex items-center gap-1 justify-end">
                        <span className="text-muted-industrial">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={quickEdit.value}
                          autoFocus
                          onChange={(e) => setQuickEdit({ id: i.id, value: e.target.value })}
                          onBlur={() => saveQuickCost(i)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveQuickCost(i);
                            if (e.key === "Escape") setQuickEdit({ id: null, value: "" });
                          }}
                          className="rounded-none bg-transparent border-primary-orange mono w-24 h-7 text-xs text-right"
                          data-testid={`inline-cost-${i.sku}`}
                        />
                        <span className="text-[10px] text-muted-industrial">/{unit}</span>
                      </div>
                    ) : (
                      <>
                        ${cost.toLocaleString(undefined, { maximumFractionDigits: 2 })}<span className="text-[10px] text-muted-industrial">/{unit}</span>
                      </>
                    )}
                  </td>
                  <td className="p-3 mono text-[10px] text-muted-industrial">{i.gl_code || "—"}</td>
                  <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1 justify-end">
                      <Input
                        type="number"
                        step={bulk ? "0.1" : "1"}
                        placeholder={`± ${unit}`}
                        value={adjust.id === i.id ? adjust.delta : ""}
                        onChange={(e) => setAdjust({ id: i.id, delta: parseFloat(e.target.value || 0) })}
                        className="rounded-none bg-transparent border-industrial mono w-20 h-7 text-xs"
                      />
                      <Button data-testid={`adjust-${i.sku}`} size="sm" onClick={() => doAdjust(i, adjust.delta)} className="rounded-none bg-surface-2 hover:bg-surface border border-industrial uppercase text-xs h-7">Go</Button>
                      <Button data-testid={`set-count-${i.sku}`} size="sm" variant="outline" onClick={() => openCount(i)} title="Correct stock to the actual silo reading / physical count" className={`rounded-none border-industrial uppercase text-xs h-7 ${Number(i.quantity) <= 0 ? "border-red-500 text-red-400" : ""}`}>Set count</Button>
                    </div>
                  </td>
                  <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      onClick={() => openEdit(i)}
                      data-testid={`edit-inv-${i.sku}`}
                      className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold h-7 px-3"
                    >
                      Edit
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="p-3 text-[11px] text-muted-industrial border-t border-industrial">
          Bulk materials (cement, sand, man-dust, aggregates) are weighed in <span className="mono text-primary-orange">tonnes (t)</span>. Admixtures stay in L / kg.
        </div>
      </div>

      {/* ─── Receipt History ─────────────────────────────────────── */}
      <div className="card-industrial overflow-x-auto" data-testid="receipt-history">
        <div className="p-3 border-b border-industrial flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-primary-orange" />
            <div className="label-tag">Receipt History</div>
          </div>
          <div className="mono text-[10px] text-muted-industrial uppercase tracking-widest">
            Latest {receipts.length} goods-in
          </div>
        </div>
        {receipts.length === 0 ? (
          <div className="p-6 text-center text-sm mono text-muted-industrial">
            No receipts yet. Click <span className="text-primary-orange">Receipt Goods</span> after a delivery to log the drop.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-industrial"><tr className="text-left">
              <th className="p-3 label-tag">When</th>
              <th className="p-3 label-tag">Supplier</th>
              <th className="p-3 label-tag">Docket</th>
              <th className="p-3 label-tag">Material</th>
              <th className="p-3 label-tag text-right">Qty</th>
              <th className="p-3 label-tag text-right">Unit Cost</th>
              <th className="p-3 label-tag">By</th>
            </tr></thead>
            <tbody>
              {receipts.map((r) => {
                const bulk = BULK_CATS.has(r.category);
                const displayQty = bulk ? (r.quantity || 0) / 1000 : (r.quantity || 0);
                const displayCost = bulk ? (r.unit_cost || 0) * 1000 : (r.unit_cost || 0);
                const unit = bulk ? "t" : (r.unit || "");
                return (
                  <tr key={r.id} data-testid={`receipt-row-${r.id}`} className="border-b border-industrial hover:bg-surface-2">
                    <td className="p-3 mono text-xs">{formatLocalDateTime(r.received_at)}</td>
                    <td className="p-3 mono font-bold">{r.supplier}</td>
                    <td className="p-3 mono text-xs text-muted-industrial">{r.docket_ref || "—"}</td>
                    <td className="p-3">
                      <span className="mono text-xs text-muted-industrial">{r.sku}</span>
                      <span className="ml-1.5">{r.name}</span>
                    </td>
                    <td className="p-3 mono text-right">
                      <span className="text-primary-orange font-bold">
                        +{displayQty.toLocaleString(undefined, { maximumFractionDigits: bulk ? 2 : 0 })}
                      </span>
                      <span className="text-[10px] text-muted-industrial ml-1">{unit}</span>
                    </td>
                    <td className="p-3 mono text-right">
                      ${displayCost.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      <span className="text-[10px] text-muted-industrial">/{unit}</span>
                    </td>
                    <td className="p-3 mono text-xs text-muted-industrial">{r.received_by_name || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ─── Receipt Goods dialog ────────────────────────────────── */}
      <Dialog open={receiptOpen} onOpenChange={setReceiptOpen}>
        <DialogContent className="rounded-none bg-surface border-industrial max-w-lg" data-testid="receipt-dialog">
          <DialogHeader>
            <DialogTitle className="heading uppercase inline-flex items-center gap-2">
              <PackagePlus className="w-5 h-5 text-primary-orange" /> Receipt Goods
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="label-tag">Material</Label>
              <Select value={receipt.inventory_id} onValueChange={(v) => setReceipt({ ...receipt, inventory_id: v, unit_cost: "" })}>
                <SelectTrigger className="rounded-none bg-transparent border-industrial mono" data-testid="receipt-material"><SelectValue placeholder="Select material" /></SelectTrigger>
                <SelectContent className="rounded-none bg-surface border-industrial max-h-80">
                  {list.map((it) => (
                    <SelectItem key={it.id} value={it.id}>
                      {it.sku} · {it.name}
                      <span className="text-muted-industrial mono text-xs ml-2">
                        ({toDisplayQty(it).toLocaleString(undefined, { maximumFractionDigits: isBulk(it.category) ? 2 : 0 })} {displayUnit(it)} on hand)
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-tag">Supplier</Label>
              <Input
                data-testid="receipt-supplier"
                value={receipt.supplier}
                onChange={(e) => setReceipt({ ...receipt, supplier: e.target.value })}
                placeholder="e.g. Hanson, Holcim, Boral"
                className="rounded-none bg-transparent border-industrial mono"
              />
            </div>
            <div>
              <Label className="label-tag">Docket / Reference</Label>
              <Input
                data-testid="receipt-docket"
                value={receipt.docket_ref}
                onChange={(e) => setReceipt({ ...receipt, docket_ref: e.target.value })}
                placeholder="Optional"
                className="rounded-none bg-transparent border-industrial mono"
              />
            </div>
            <div>
              <Label className="label-tag">
                Quantity ({receiptItem ? displayUnit(receiptItem) : "—"})
              </Label>
              <Input
                data-testid="receipt-qty"
                type="number"
                step={receiptItem && isBulk(receiptItem.category) ? "0.1" : "1"}
                value={receipt.quantity}
                onChange={(e) => setReceipt({ ...receipt, quantity: e.target.value })}
                placeholder="0.00"
                className="rounded-none bg-transparent border-industrial mono"
              />
            </div>
            <div>
              <Label className="label-tag">
                Unit Cost — leave blank to keep
                {receiptItem ? ` ($${toDisplayCost(receiptItem).toLocaleString(undefined, { maximumFractionDigits: 2 })}/${displayUnit(receiptItem)})` : ""}
              </Label>
              <Input
                data-testid="receipt-cost"
                type="number"
                step="0.01"
                value={receipt.unit_cost}
                onChange={(e) => setReceipt({ ...receipt, unit_cost: e.target.value })}
                placeholder={receiptItem ? `$/${displayUnit(receiptItem)}` : "—"}
                className="rounded-none bg-transparent border-industrial mono"
              />
            </div>
            <div className="col-span-2">
              <Label className="label-tag">Notes (optional)</Label>
              <Input
                data-testid="receipt-notes"
                value={receipt.notes}
                onChange={(e) => setReceipt({ ...receipt, notes: e.target.value })}
                placeholder="Batch #, lot, tanker rego…"
                className="rounded-none bg-transparent border-industrial mono"
              />
            </div>
            {receiptItem && Number.isFinite(parseFloat(receipt.quantity)) && parseFloat(receipt.quantity) > 0 && (
              <div className="col-span-2 border border-primary-orange/40 bg-primary-orange/5 px-3 py-2 mono text-xs text-ink">
                On confirm →{" "}
                <span className="text-primary-orange font-bold">
                  {receiptItem.sku}
                </span>{" "}
                on-hand rises from{" "}
                <b>{toDisplayQty(receiptItem).toLocaleString(undefined, { maximumFractionDigits: isBulk(receiptItem.category) ? 2 : 0 })}</b>{" "}
                to{" "}
                <b>
                  {(toDisplayQty(receiptItem) + parseFloat(receipt.quantity)).toLocaleString(undefined, { maximumFractionDigits: isBulk(receiptItem.category) ? 2 : 0 })}
                </b>{" "}
                {displayUnit(receiptItem)}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReceiptOpen(false)}
              className="rounded-none border-industrial uppercase tracking-widest text-xs"
            >
              Cancel
            </Button>
            <Button
              data-testid="receipt-confirm"
              onClick={submitReceipt}
              disabled={receiptSaving}
              className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold disabled:opacity-40"
            >
              {receiptSaving ? "Booking…" : "Confirm Receipt"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!countTarget} onOpenChange={(v) => { if (!v) setCountTarget(null); }}>
        <DialogContent className="rounded-none bg-surface border-industrial max-w-md" data-testid="set-count-dialog">
          <DialogHeader><DialogTitle className="heading uppercase">Stock correction · {countTarget?.name}</DialogTitle></DialogHeader>
          <p className="mono text-[11px] text-muted-industrial">Enter what is physically on hand. The system level is replaced and the change is logged as a correction (stock can never sit below zero).</p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-tag">Actual on hand ({countTarget ? displayUnit(countTarget) : ""})</Label>
              <Input type="number" step="0.1" min="0" value={countForm.amount} onChange={(e) => setCountForm({ ...countForm, amount: e.target.value })} className="rounded-none bg-transparent border-industrial mono" data-testid="set-count-amount" /></div>
            <div><Label className="label-tag">Reason</Label>
              <Input value={countForm.reason} onChange={(e) => setCountForm({ ...countForm, reason: e.target.value })} className="rounded-none bg-transparent border-industrial mono" data-testid="set-count-reason" /></div>
          </div>
          <DialogFooter><Button onClick={saveCount} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold" data-testid="set-count-save">Save correction</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <InventoryImportDialog open={importOpen} onOpenChange={setImportOpen} plantId={plantId} onDone={load} />
      <MergeDuplicatesDialog open={mergeOpen} onOpenChange={setMergeOpen} onDone={load} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none bg-surface border-industrial max-w-lg">
          <DialogHeader><DialogTitle className="heading uppercase">{editing ? "Edit" : "New"} Item</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="label-tag">SKU</Label><Input data-testid="inv-sku" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="rounded-none bg-transparent border-industrial mono" /></div>
            <div><Label className="label-tag">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none bg-transparent border-industrial mono" /></div>
            <div><Label className="label-tag">Category</Label>
              <Select
                value={form.category}
                onValueChange={(v) => {
                  // When switching between bulk / non-bulk, snap the unit into a sane default.
                  const nextUnit = isBulk(v) ? "t" : (v === "admixture" ? "L" : "kg");
                  setForm({ ...form, category: v, unit: nextUnit });
                }}
              >
                <SelectTrigger className="rounded-none bg-transparent border-industrial"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none bg-surface border-industrial">{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-tag">Unit</Label>
              <Input
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                disabled={isBulk(form.category)}
                className="rounded-none bg-transparent border-industrial mono disabled:opacity-60"
              />
            </div>
            <div>
              <Label className="label-tag">Quantity ({isBulk(form.category) ? "tonnes" : form.unit})</Label>
              <Input type="number" step={isBulk(form.category) ? "0.1" : "1"} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: parseFloat(e.target.value) })} className="rounded-none bg-transparent border-industrial mono" />
            </div>
            <div>
              <Label className="label-tag">Low Threshold ({isBulk(form.category) ? "tonnes" : form.unit})</Label>
              <Input type="number" step={isBulk(form.category) ? "0.1" : "1"} value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: parseFloat(e.target.value) })} className="rounded-none bg-transparent border-industrial mono" />
            </div>
            <div>
              <Label className="label-tag">Unit Cost ($ / {isBulk(form.category) ? "tonne" : form.unit})</Label>
              <Input type="number" step="0.01" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: parseFloat(e.target.value) })} className="rounded-none bg-transparent border-industrial mono" />
            </div>
            <div className="col-span-2"><Label className="label-tag">GL / Export Code (Xero / MYOB)</Label><Input data-testid="inv-gl-code" value={form.gl_code} onChange={(e) => setForm({ ...form, gl_code: e.target.value })} placeholder="e.g. 5100 · COGS - Cement" className="rounded-none bg-transparent border-industrial mono" /></div>
            {form.category === "cement" && (
              <>
                <div>
                  <Label className="label-tag">Silo</Label>
                  <select data-testid="inv-silo" value={form.silo || ""} onChange={(e) => setForm({ ...form, silo: e.target.value })}
                    className="w-full h-10 rounded-none bg-transparent border border-industrial mono text-sm px-2">
                    <option value="">— not assigned —</option>
                    <option value="A">Silo A</option>
                    <option value="B">Silo B</option>
                  </select>
                  <div className="text-[10px] text-muted-industrial mt-1">Tag both silos and the cockpit shows each one's real level — batching then draws off the silo the lane selected</div>
                </div>
                <div>
                  <Label className="label-tag">Silo capacity (t)</Label>
                  <Input type="number" step="0.5" data-testid="inv-capacity" value={form.capacity_kg}
                    onChange={(e) => setForm({ ...form, capacity_kg: e.target.value })} placeholder="60"
                    className="rounded-none bg-transparent border-industrial mono" />
                  <div className="text-[10px] text-muted-industrial mt-1">What it holds when full — drives the cockpit percentage</div>
                </div>
              </>
            )}
          </div>
          <DialogFooter className="sm:justify-between">
            {editing ? <Button data-testid="delete-inv-btn" variant="outline" onClick={remove} className="rounded-none border-red-600 text-red-400 hover:bg-red-950/40 uppercase tracking-widest text-xs">Delete item</Button> : <span />}
            <Button data-testid="save-inv-btn" onClick={save} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
